import React from 'react';
import useDocumentTitle from '../hooks/useDocumentTitle';
import SEOHead from '../components/SEOHead';

const PrivacyPolicy: React.FC = () => {
  useDocumentTitle('Cognidaily | Privacy Policy');
  const lastUpdated = 'January 2025';

  return (
    <>
      <SEOHead
        title="Privacy Policy"
        description="Learn how Cognidaily collects, uses, and protects your personal information. Our commitment to your privacy and data security."
        keywords="privacy policy, data protection, GDPR, CCPA, user privacy, data security"
        url="https://cognidaily.io/privacy-policy"
      />

      <section>
        <h1>Privacy Policy</h1>
        <p>Last Updated: {lastUpdated}</p>
        <div>
              <p>
                At Cognidaily, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
              </p>
            </div>

            <div className="legal-content">
              <section className="legal-content-section">
                <h2>1. Information We Collect</h2>
                
                <h3>1.1 Account Information</h3>
                <p>
                  When you create an account, we collect information such as your email address, username, and password. This information is necessary to create and manage your account.
                </p>

                <h3>1.2 Quiz and Performance Data</h3>
                <p>
                  We collect data related to your quiz participation, including:
                </p>
                <ul>
                  <li>Quiz scores and accuracy</li>
                  <li>Response times</li>
                  <li>Questions answered correctly or incorrectly</li>
                  <li>Leaderboard rankings</li>
                  <li>Badge achievements and XP progress</li>
                </ul>

                <h3>1.3 Usage Data</h3>
                <p>
                  We automatically collect certain information when you use our platform, including:
                </p>
                <ul>
                  <li>Device information (browser type, operating system)</li>
                  <li>IP address and general location data</li>
                  <li>Pages visited and features used</li>
                  <li>Time spent on the platform</li>
                  <li>Referral sources</li>
                </ul>

                <h3>1.4 Cookies and Tracking Technologies</h3>
                <p>
                  We use cookies and similar tracking technologies to enhance your experience. For detailed information about our use of cookies, please see our <a href="/cookie-policy">Cookie Policy</a>.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>2. How We Use Your Information</h2>
                
                <p>We use the information we collect for the following purposes:</p>
                
                <h3>2.1 Service Provision</h3>
                <ul>
                  <li>To provide and maintain our quiz platform</li>
                  <li>To process your quiz attempts and calculate scores</li>
                  <li>To display leaderboards and rankings</li>
                  <li>To track your progress, XP, and achievements</li>
                </ul>

                <h3>2.2 Personalization</h3>
                <ul>
                  <li>To customize your experience on the platform</li>
                  <li>To provide relevant content and features</li>
                  <li>To improve our question selection and difficulty</li>
                </ul>

                <h3>2.3 Communication</h3>
                <ul>
                  <li>To send you important updates about the service</li>
                  <li>To respond to your inquiries and support requests</li>
                  <li>To send notifications about your achievements and progress</li>
                </ul>

                <h3>2.4 Analytics and Improvement</h3>
                <ul>
                  <li>To analyze platform usage and performance</li>
                  <li>To identify and fix technical issues</li>
                  <li>To develop new features and improve existing ones</li>
                </ul>

                <h3>2.5 Legal Compliance</h3>
                <ul>
                  <li>To comply with applicable laws and regulations</li>
                  <li>To enforce our Terms of Service</li>
                  <li>To protect our rights and prevent fraud</li>
                </ul>
              </section>

              <section className="legal-content-section">
                <h2>3. Information Sharing and Disclosure</h2>
                
                <p>We do not sell your personal information. We may share your information in the following circumstances:</p>

                <h3>3.1 Public Information</h3>
                <p>
                  Certain information is publicly visible on leaderboards, including your username, scores, rankings, and achievements. This information is essential to the competitive nature of our platform.
                </p>

                <h3>3.2 Service Providers</h3>
                <p>
                  We may share information with third-party service providers who assist us in operating our platform, such as:
                </p>
                <ul>
                  <li>Hosting and cloud storage providers</li>
                  <li>Analytics services</li>
                  <li>Email delivery services</li>
                  <li>Payment processors (if applicable)</li>
                </ul>
                <p>
                  These service providers are contractually obligated to protect your information and use it only for the purposes we specify.
                </p>

                <h3>3.3 Legal Requirements</h3>
                <p>
                  We may disclose your information if required by law, court order, or government regulation, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.
                </p>

                <h3>3.4 Business Transfers</h3>
                <p>
                  In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity, subject to the same privacy protections.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>4. Your Rights and Choices</h2>
                
                <h3>4.1 Access and Correction</h3>
                <p>
                  You can access and update your account information at any time through your profile settings.
                </p>

                <h3>4.2 Data Deletion</h3>
                <p>
                  You may request deletion of your account and associated data by contacting us at <a href="mailto:hello@cognidaily.io">hello@cognidaily.io</a>. We will process your request in accordance with applicable law.
                </p>

                <h3>4.3 GDPR Rights (European Users)</h3>
                <p>
                  If you are located in the European Economic Area, you have the following rights:
                </p>
                <ul>
                  <li>Right to access your personal data</li>
                  <li>Right to rectify inaccurate data</li>
                  <li>Right to erasure ("right to be forgotten")</li>
                  <li>Right to restrict processing</li>
                  <li>Right to data portability</li>
                  <li>Right to object to processing</li>
                </ul>
                <p>
                  To exercise these rights, please contact us at <a href="mailto:hello@cognidaily.io">hello@cognidaily.io</a>.
                </p>

                <h3>4.4 CCPA Rights (California Users)</h3>
                <p>
                  If you are a California resident, you have the right to:
                </p>
                <ul>
                  <li>Know what personal information we collect</li>
                  <li>Know whether we sell or disclose your personal information</li>
                  <li>Opt-out of the sale of personal information (we do not sell personal information)</li>
                  <li>Access and delete your personal information</li>
                  <li>Non-discrimination for exercising your privacy rights</li>
                </ul>

                <h3>4.5 Cookie Preferences</h3>
                <p>
                  You can manage your cookie preferences through your browser settings. For more information, see our <a href="/cookie-policy">Cookie Policy</a>.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>5. Data Security</h2>
                
                <p>
                  We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                </p>
                <ul>
                  <li>Encryption of data in transit using SSL/TLS</li>
                  <li>Secure password storage using industry-standard hashing</li>
                  <li>Regular security assessments and updates</li>
                  <li>Access controls and authentication mechanisms</li>
                  <li>Secure hosting infrastructure</li>
                </ul>
                <p>
                  However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>6. Data Retention</h2>
                
                <p>
                  We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When you delete your account, we will delete or anonymize your personal information, except where we are required to retain it for legal purposes.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>7. Children's Privacy</h2>
                
                <p>
                  Our platform is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately, and we will take steps to delete such information.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>8. International Data Transfers</h2>
                
                <p>
                  Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. We take appropriate safeguards to ensure your information receives adequate protection in accordance with this Privacy Policy.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>9. Changes to This Privacy Policy</h2>
                
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use of the platform after such changes constitutes your acceptance of the updated Privacy Policy.
                </p>
              </section>

              <section className="legal-content-section">
                <h2>10. Contact Us</h2>
                
                <p>
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:
                </p>
                <p>
                  <strong>Email:</strong> <a href="mailto:hello@cognidaily.io">hello@cognidaily.io</a>
                </p>
                <p>
                  We will respond to your inquiry as soon as possible.
                </p>
              </section>
            </div>
      </section>
    </>
  );
};

export default PrivacyPolicy;

